import { TRANSLATION_LOCALES, type ContentLocale } from './contentLocales';

/**
 * QA-122 (b안) — per-locale 본문 맵 ↔ BE translations 맵 변환 헬퍼.
 *
 * BE translations 는 `Record<locale, { ...fields }>` 형태(Notice/FAQ/Popup 등 공통). 본 헬퍼는
 * 그 중 단일 HTML 필드(예: content/answerHtml/descriptionHtml/body)만 다루는 폼을 위한 것으로,
 * 필드명을 인자로 받아 계약별 키에 무관하게 동작한다(추정 금지 — 호스트가 키를 명시).
 */

/**
 * 응답 translations 맵에서 번역 로케일별 단일 HTML 필드만 추출(en/ja/zh). 빈/누락 제외.
 * 값은 unknown 으로 받아 안전 narrowing — 호출처 DTO(인터페이스)와 무관하게 동작한다.
 */
export const pickLocaleHtml = (
  translations: Readonly<Record<string, unknown>> | null | undefined,
  field: string,
): Partial<Record<ContentLocale, string>> => {
  const out: Partial<Record<ContentLocale, string>> = {};
  if (!translations) return out;
  for (const loc of TRANSLATION_LOCALES) {
    const entry = translations[loc];
    if (entry == null || typeof entry !== 'object') continue;
    const value = (entry as Record<string, unknown>)[field];
    if (typeof value === 'string' && value.trim()) out[loc] = value;
  }
  return out;
};

/**
 * 폼의 로케일별 HTML 을 요청 translations 맵으로 — 빈 본문 로케일은 제외(SF ko 폴백).
 * 결과는 `{ [locale]: { [field]: html } }`. 모든 로케일이 비면 undefined(요청에서 생략).
 * 반환 객체 타입은 호스트가 자신의 Translation DTO(T)로 단언한다(필드 1개만 채움).
 */
export const buildLocaleHtmlTranslations = <T>(
  contentByLocale: Partial<Record<ContentLocale, string>>,
  field: string,
): Record<string, T> | undefined => {
  const entries: [string, T][] = [];
  for (const loc of TRANSLATION_LOCALES) {
    const html = contentByLocale[loc];
    if (html && html.trim()) entries.push([loc, { [field]: html } as T]);
  }
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};

/**
 * 다중 필드(예: {title, body} / {title, description})를 로케일별로 묶어 translations 맵을 만든다.
 * 각 필드는 `{ field: byLocaleMap }` 으로 전달한다. 한 로케일에서 모든 필드가 비면 그 로케일은 생략
 * (SF ko 폴백). 어느 한 필드라도 값이 있으면 그 로케일 dto 에 채워진 필드만 담는다.
 */
export const buildLocaleMultiFieldTranslations = <T>(
  fieldMaps: Record<string, Partial<Record<ContentLocale, string>>>,
): Record<string, T> | undefined => {
  const fields = Object.keys(fieldMaps);
  const entries: [string, T][] = [];
  for (const loc of TRANSLATION_LOCALES) {
    const dto: Record<string, string> = {};
    for (const field of fields) {
      const value = fieldMaps[field]?.[loc];
      if (value && value.trim()) dto[field] = value;
    }
    if (Object.keys(dto).length > 0) entries.push([loc, dto as T]);
  }
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};
